import { InputType } from '../../../abacus/enums/input-type.enum'
import {
  CheckboxAnswer,
  CheckboxItem,
  MatrixConfig,
  QuestionnaireField,
  QuestionnaireQuestion,
  RadioItem
} from '../models/questionnaire.model'

export function mapQuestionsToFields(
  questions: QuestionnaireQuestion[]
): QuestionnaireField[] {
  const fields: QuestionnaireField[] = []

  const topLevel = questions.filter(
    (q) => q.parentQuestionId === null || q.parentQuestionId === undefined
  )

  for (const question of topLevel) {
    fields.push(...mapSingleQuestion(question))
    flattenChildren(question, fields)
  }

  return fields
}

function flattenChildren(
  question: QuestionnaireQuestion,
  fields: QuestionnaireField[],
  depth: number = 1
): void {
  if (!question.children?.length) return
  const sorted = [...question.children].sort((a, b) => a.order - b.order)
  for (const child of sorted) {
    const childFields = mapSingleQuestion(child)
    for (const cf of childFields) {
      cf.indentLevel = depth
      if (child.showWhen) {
        cf.showWhenRule = child.showWhen
        cf.hidden = true
      }
      fields.push(cf)
    }
    flattenChildren(child, fields, depth + 1)
  }
}

function mapSingleQuestion(question: QuestionnaireQuestion): QuestionnaireField[] {
  const fields: QuestionnaireField[] = []
  const prefix = question.prefix?.replace(/\.+$/, '')
  const label = prefix
    ? `${prefix}. ${question.title}`
    : question.title

  const meta: Partial<QuestionnaireField> = {}
  if (question.collectRefs) meta.collectRefs = true
  if (question.recommendedActors?.length) meta.recommendedActors = question.recommendedActors
  if (question.footnotes?.length) meta.footnotes = question.footnotes

  if (question.type === 'empty') {
    fields.push({
      property: question.questionKey,
      id: question.questionKey,
      questionId: question.id,
      label,
      inputType: InputType.Text,
      className: 'is-12',
      isHeading: true,
      ...meta
    })
    return fields
  }

  if (question.type === 'text') {
    fields.push({
      property: question.questionKey,
      id: question.questionKey,
      questionId: question.id,
      label,
      renderLabelAsHtml: true,
      editorMinHeight: 150,
      inputType: InputType.RichText,
      className: 'is-12',
      required: question.required,
      ...meta
    })
  } else if (question.type === 'radio') {
    fields.push({
      property: question.questionKey,
      id: question.questionKey,
      questionId: question.id,
      label,
      renderLabelAsHtml: true,
      inputType: InputType.Radio,
      className: 'is-12',
      required: question.required,
      selectOptions: ((question.items as RadioItem[]) || []).map((item) => ({
        label: item.label,
        value: item.value ?? item.label
      })),
      ...meta
    })
  } else if (question.type === 'checkbox') {
    const items = question.items as CheckboxItem[]
    fields.push({
      property: question.questionKey,
      id: question.questionKey,
      questionId: question.id,
      label,
      renderLabelAsHtml: true,
      inputType: InputType.MultiCheckbox,
      className: 'is-12',
      required: question.required,
      selectOptions: (items || []).map((item) => ({
        label: item.label,
        value: item.value,
        ...(item.description ? { description: item.description } : {})
      })),
      checkboxItems: items,
      isCheckbox: true,
      specifyText: {},
      ...meta
    })
  } else if (question.type === 'matrix') {
    fields.push({
      property: question.questionKey,
      id: question.questionKey,
      questionId: question.id,
      label,
      renderLabelAsHtml: true,
      inputType: InputType.Text,
      className: 'is-12',
      required: question.required,
      matrixConfig: question.items as MatrixConfig,
      isMatrix: true,
      matrixValue: {},
      matrixSpecifyText: {},
      ...meta
    })
  }

  return fields
}

export function populateFieldValues(
  fields: QuestionnaireField[],
  answersByQuestionId: Record<number, string>
): void {
  for (const field of fields) {
    if (field.questionId !== undefined && answersByQuestionId[field.questionId] !== undefined) {
      const raw = answersByQuestionId[field.questionId]

      if (field.isCheckbox) {
        try {
          const parsed: CheckboxAnswer = JSON.parse(raw)
          field.initialValue = parsed.selected || []
          field.specifyText = parsed.specifyText || {}
        } catch {
          field.initialValue = raw
        }
      } else if (field.isMatrix) {
        try {
          const parsed = JSON.parse(raw)
          if (parsed.selections) {
            field.matrixValue = parsed.selections
            field.matrixSpecifyText = parsed.specifyText || {}
          } else {
            field.matrixValue = parsed
            field.matrixSpecifyText = {}
          }
          field.initialValue = raw
        } catch {
          field.matrixValue = {}
          field.matrixSpecifyText = {}
        }
      } else {
        field.initialValue = raw
      }
    }
  }
}
